import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

import { 
  Loader2, 
  RotateCcw, 
  Plus, 

  AlertTriangle,
  History
} from "lucide-react";
import { getDoc, doc, collection, getDocs, query, where } from "firebase/firestore";
import { db } from "@/firebase/config";
import { calculateFeeBalances, TermTotals } from "@/utils/feeCalculations";

interface PaymentSimulatorProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: string;
  studentName: string;
  schoolId: string;
  // Overrides to match complex logic from parent
  forcedFeeTotals?: { term1: number; term2: number; term3: number; annual: number };
  forcedOpeningBalance?: number;
}

interface MockTransaction {
  id: string;
  amount: number;
  date: Date;
  type: string;
  method: string;
  isMock: boolean;
}

interface SimulatorState {
  studentData: any;
  feeStructure: any;
  transactions: MockTransaction[];
  calculatedFees: any; // Result of calculateFeeBalances
  futureChain?: { classObj: any; matchingLevel: any }[]; // Pre-fetched promotion chain
  futureYears?: { className: string; calculatedFees: any; isPredicted: boolean; isCurrentSim: boolean }[]; // Calculated predictions
  simulatedTerm?: 'term1' | 'term2' | 'term3'; // Optional simulated term override
  simulatedYearOffset?: number; // 0 = Current year, 1 = Next class, etc.
}

const PaymentSimulator = ({ isOpen, onClose, studentId, studentName, schoolId, forcedFeeTotals, forcedOpeningBalance }: PaymentSimulatorProps) => {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<SimulatorState | null>(null);
  
  // New Payment Form
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");

  // Load Real Data (Snapshot)
  useEffect(() => {
    if (isOpen && studentId && schoolId) {
      loadSnapshot();
    }
  }, [isOpen, studentId, schoolId]);

  const loadSnapshot = async () => {
    setLoading(true);
    try {
      // 1. Fetch Student Data
      const studentDoc = await getDoc(doc(db, `schools/${schoolId}/students/${studentId}`));
      const studentData = studentDoc.exists() ? studentDoc.data() : null;

      if (!studentData) throw new Error("Student not found");

      // 2. Fetch Fee Structure (Current Year Preference)
      const currentYear = new Date().getFullYear().toString();
      let feeDoc = await getDoc(doc(db, `schools/${schoolId}/deployedFeeStructures/${currentYear}`));
      if (!feeDoc.exists()) {
        feeDoc = await getDoc(doc(db, `schools/${schoolId}/deployedFeeStructures/current`));
      }
      const feeData = feeDoc.exists() ? feeDoc.data() : { levelFees: [] };

      // 3. Fetch Real Transactions
      const transactions: MockTransaction[] = [];
      const providers = ['mpesa', 'equity', 'kcb', 'cash', 'card', 'bank'];
      const regNo = studentData.registrationNumber;

      // Parallel fetch for speed
      await Promise.all(providers.map(async (provider) => {
        const q = query(
          collection(db, `schools/${schoolId}/transactions/${provider}/records`),
          where('accountReference', '==', `Fee-${regNo}`),
          where('status', '==', 'completed')
        );
        const snap = await getDocs(q);
        snap.forEach(d => {
           const data = d.data();
           transactions.push({
             id: d.id,
             amount: Number(data.amount) || 0,
             date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
             type: data.type || "credit",
             method: provider,
             isMock: false
           });
        });
      }));

      // Find matching fee level
      const studentClass = studentData.className || studentData.class || "";
      const levelFees = feeData.levelFees || [];
      const matchingLevel = levelFees.find((lvl: any) => 
         lvl.classes && lvl.classes.some((c: string) => 
            c.toLowerCase() === studentClass.toLowerCase() || 
            studentClass.toLowerCase().includes(c.toLowerCase())
         )
      ) || levelFees.find((lvl: any) => lvl.level === (studentData.grade || studentData.admissionGrade));

      // 4. Determine Future Promotion Chain
      const futureChain: { classObj: any; matchingLevel: any }[] = [];
      try {
        const classesSnap = await getDocs(collection(db, `schools/${schoolId}/classes`));
        const classesList = classesSnap.docs.map(d => d.data());
        let currentClassObj = classesList.find(c => c.name === studentClass);
        
        while (currentClassObj && currentClassObj.nextClassUid && currentClassObj.nextClassUid !== 'ALUMNI') {
          const nextClass = classesList.find(c => c.classUid === currentClassObj!.nextClassUid);
          if (nextClass) {
            const nextMatchingLevel = levelFees.find((lvl: any) => 
               lvl.classes && lvl.classes.some((c: string) => 
                  c.toLowerCase() === nextClass.name.toLowerCase() || 
                  nextClass.name.toLowerCase().includes(c.toLowerCase())
               )
            );
            
            if (nextMatchingLevel) {
              futureChain.push({ classObj: nextClass, matchingLevel: nextMatchingLevel });
            }
            currentClassObj = nextClass;
          } else {
             break;
          }
        }
      } catch (e) {
        console.warn("Could not load future class chain", e);
      }

      setState({
        studentData: { ...studentData, matchingLevel },
        feeStructure: feeData,
        transactions: transactions.sort((a,b) => b.date.getTime() - a.date.getTime()),
        calculatedFees: null, // Will calculate below
        futureChain
      });

    } catch (err) {
      console.error("Simulation Error", err);
    } finally {
      setLoading(false);
    }
  };

  // Recalculate whenever state changes
  useEffect(() => {
    if (!state) return;

    const { studentData, transactions } = state;
    const matchingLevel = studentData.matchingLevel;

    if (!matchingLevel) {
       setState(prev => prev ? ({ ...prev, calculatedFees: null }) : null);
       return;
    }

    // Reuse logic from GuardianFinance/feeCalculations
    const isOngoing = !!studentData.ongoing;
    const categories = matchingLevel.categories || [];
    const isDay = studentData.studentType === "Day School";

    // 1. Calculate Required Fees
    // PRIORITY: Use forced totals from parent if available (guarantees match with Dashboard)
    let totals: TermTotals;

    if (forcedFeeTotals) {
       totals = { ...forcedFeeTotals };
    } else {
        // Fallback: Simple Sim Logic
        let term1Req = 0, term2Req = 0, term3Req = 0;
        categories.forEach((cat: any) => {
          const v1 = isDay ? (cat.daySchoolTerm1 ?? cat.term1 ?? 0) : (cat.term1 ?? 0);
          const v2 = isDay ? (cat.daySchoolTerm2 ?? cat.term2 ?? 0) : (cat.term2 ?? 0);
          const v3 = isDay ? (cat.daySchoolTerm3 ?? cat.term3 ?? 0) : (cat.term3 ?? 0);

          term1Req += Number(v1) || 0;
          term2Req += Number(v2) || 0;
          term3Req += Number(v3) || 0;
        });

        totals = {
           term1: isOngoing ? 0 : term1Req,
           term2: isOngoing ? 0 : term2Req,
           term3: isOngoing ? 0 : term3Req,
           annual: 0
        };
    }

    // 2. Calculate Payments
    // Sort transactions by date ASC for proper flow? Actually logic is aggregate usually
    // But calculateFeeBalances expects {term1: X, term2: Y...}
    // We allocation strategy: First -> Overflow -> Next
    
    let totalPaid = transactions.reduce((sum, t) => sum + (t.type === 'debit' ? -t.amount : t.amount), 0);
    
    // Simple allocation: T1, then T2, then T3
    // Wait `calculateFeeBalances` handles the flow if we pass `totals`
    // Actually `calculateFeeBalances` takes `paid: {term1, term2, term3}` which means WE have to allocate
    // Or we use a simpler accumulation logic.
    
    // Let's manually flow it:
    let paymentPool = totalPaid;
    
    // T1
    const t1Paid = Math.min(paymentPool, totals.term1);
    paymentPool -= t1Paid;
    
    // T2
    const t2Paid = Math.min(paymentPool, totals.term2);
    paymentPool -= t2Paid;
    
    // T3
    const t3Paid = Math.min(paymentPool, totals.term3);
    paymentPool -= t3Paid;
    
    // Pass to utility


    // Override if we have extra credit
    // If paymentPool > 0, it reduces the final term's balance even further (credit)
    // The utility handles "netBalance", but if we paid MORE than required, t3Paid is capped at required inside logic?
    // Let's re-check utility `calculateFeeBalances`.
    // It subtracts paid from due.
    // If input `term1Paid` > `term1Required`, then `term1NetBalance` < 0 (Credit).
    // So we should NOT min-cap input payments.
    
    // Improved Allocation Strategy for Sim:
    // 1. Pay Off Brought Forward debt first? (Utility handles usage BF in T1)
    // 2. Pay T1, then T2...
    
    // Actually the logic in GuardianFinance often sums up total paid and allocates it visually
    // But `calculateFeeBalances` helps flow it.
    
    // Let's allocate freely
    paymentPool = totalPaid;
    
    // Let's stick to: Fill buckets.
    const paidT1 = Math.min(Math.max(0, paymentPool), totals.term1 + (studentData.outstandingBalance || 0));
    paymentPool -= paidT1;
    
    const paidT2 = Math.min(Math.max(0, paymentPool), totals.term2);
    paymentPool -= paidT2;
    
    const paidT3 = Math.max(0, paymentPool); // Rest goes here
    
    // Determine current term key for context-aware balance
    const realWorldTermKey = ((): 'term1' | 'term2' | 'term3' => {
        const month = new Date().getMonth();
        if (month <= 3) return 'term1'; 
        if (month <= 7) return 'term2'; 
        return 'term3'; 
    })();

    const effectiveTermKey = state.simulatedTerm || realWorldTermKey;

    // Determine opening balance (Use override if provided, else studentData)
    const effectiveOpeningBalance = forcedOpeningBalance !== undefined ? forcedOpeningBalance : (studentData.outstandingBalance || 0);

    const currentOffset = state.simulatedYearOffset || 0;

    // Sequence 1: Calculate the present year (Offset 0)
    // If we have time-traveled past Offset 0, its "effectiveTermKey" is term3 (fully matured)
    const year0TermKey = currentOffset > 0 ? 'term3' : effectiveTermKey;
    const resultYear0 = calculateFeeBalances(totals, { term1: paidT1, term2: paidT2, term3: paidT3 }, effectiveOpeningBalance, year0TermKey);
    
    // We will collect all calculated years here to display
    const futureYears: { className: string; calculatedFees: any; isPredicted: boolean; isCurrentSim: boolean }[] = [];
    
    // The balance that flows out of Year 0 into Year 1
    let rollingCredit = resultYear0.term3.balance;
    
    // Sequence 2: Calculate years we have manually Promoted to (Offset 1, 2, etc.)
    for (let yr = 1; yr <= currentOffset; yr++) {
        if (!state.futureChain || yr > state.futureChain.length) break;
        const link = state.futureChain[yr - 1]; // Offset 1 corresponds to index 0
        
        const catFuture = link.matchingLevel.categories || [];
        let nextT1Req = 0, nextT2Req = 0, nextT3Req = 0;
        
        catFuture.forEach((cat: any) => {
          const v1 = isDay ? (cat.daySchoolTerm1 ?? cat.term1 ?? 0) : (cat.term1 ?? 0);
          const v2 = isDay ? (cat.daySchoolTerm2 ?? cat.term2 ?? 0) : (cat.term2 ?? 0);
          const v3 = isDay ? (cat.daySchoolTerm3 ?? cat.term3 ?? 0) : (cat.term3 ?? 0);
          nextT1Req += Number(v1) || 0;
          nextT2Req += Number(v2) || 0;
          nextT3Req += Number(v3) || 0;
        });
        
        const nextTotals = { term1: nextT1Req, term2: nextT2Req, term3: nextT3Req, annual: 0 };
        
        // If it's a past-traveled year, it's matured to term3. If it's the *current* offset year, use the effective term key.
        const yearTermKey = yr === currentOffset ? effectiveTermKey : 'term3';
        
        // Apply carried over credit
        const nextCalc = calculateFeeBalances(nextTotals, { term1: 0, term2: 0, term3: 0 }, rollingCredit, yearTermKey);
        
        futureYears.push({
           className: link.classObj.name,
           calculatedFees: nextCalc,
           isPredicted: false, // It's an active simulation
           isCurrentSim: yr === currentOffset
        });
        
        rollingCredit = nextCalc.term3.balance; // Flow into next loop
    }
    
    // Determine the calculatedFees to bind to the primary UI block (The actively simulated year)
    // If offset = 0, it's resultYear0. If offset > 0, it's the last item in futureYears that isn't predicted.
    const activeCalculatedFees = currentOffset === 0 ? resultYear0 : futureYears[futureYears.length - 1]?.calculatedFees || resultYear0;

    // Sequence 3: Keep calculating pure future predictions IF there is still credit overflowing
    let yrIndex = currentOffset + 1;
    while (rollingCredit <= 0 && state.futureChain && yrIndex <= state.futureChain.length) {
        const link = state.futureChain[yrIndex - 1];
        
        const catFuture = link.matchingLevel.categories || [];
        let nextT1Req = 0, nextT2Req = 0, nextT3Req = 0;
        
        catFuture.forEach((cat: any) => {
          const v1 = isDay ? (cat.daySchoolTerm1 ?? cat.term1 ?? 0) : (cat.term1 ?? 0);
          const v2 = isDay ? (cat.daySchoolTerm2 ?? cat.term2 ?? 0) : (cat.term2 ?? 0);
          const v3 = isDay ? (cat.daySchoolTerm3 ?? cat.term3 ?? 0) : (cat.term3 ?? 0);
          nextT1Req += Number(v1) || 0;
          nextT2Req += Number(v2) || 0;
          nextT3Req += Number(v3) || 0;
        });
        
        const nextTotals = { term1: nextT1Req, term2: nextT2Req, term3: nextT3Req, annual: 0 };
        
        // Predictions always use term3 to gauge full year impact
        const nextCalc = calculateFeeBalances(nextTotals, { term1: 0, term2: 0, term3: 0 }, rollingCredit, 'term3');
        
        futureYears.push({
           className: link.classObj.name,
           calculatedFees: nextCalc,
           isPredicted: true,
           isCurrentSim: false
        });
        
        rollingCredit = nextCalc.term3.balance; // Flow into next loop
        if (rollingCredit > 0) break; // Credit exhausted
        
        yrIndex++;
    }
    
    // The active calculated fees for the primary display panel
    setState(prev => prev ? ({ ...prev, calculatedFees: activeCalculatedFees, futureYears }) : null);

  }, [state?.transactions, state?.studentData, state?.simulatedTerm, state?.simulatedYearOffset]);

  const addMockPayment = () => {
    if (!amount || isNaN(Number(amount))) return;
    
    const newTx: MockTransaction = {
      id: `mock-${Date.now()}`,
      amount: Number(amount),
      date: new Date(),
      type: "credit",
      method: method,
      isMock: true
    };
    
    setState(prev => prev ? ({
      ...prev,
      transactions: [newTx, ...prev.transactions]
    }) : null);
    
    setAmount("");
  };

  const promoteTerm = () => {
    setState(prev => {
      if (!prev) return prev;
      
      const realWorldTermKey = ((): 'term1' | 'term2' | 'term3' => {
          const month = new Date().getMonth();
          if (month <= 3) return 'term1'; 
          if (month <= 7) return 'term2'; 
          return 'term3'; 
      })();
      
      const current = prev.simulatedTerm || realWorldTermKey;
      let next: 'term1' | 'term2' | 'term3' | null = null;
      let nextOffset = prev.simulatedYearOffset || 0;
      
      if (current === 'term1') next = 'term2';
      else if (current === 'term2') next = 'term3';
      else if (current === 'term3') {
         // Promote to next year if we haven't hit the end of the chain
         if (prev.futureChain && nextOffset < prev.futureChain.length) {
            next = 'term1';
            nextOffset += 1;
         }
      }
      
      if (next) {
          return { ...prev, simulatedTerm: next, simulatedYearOffset: nextOffset };
      }
      return prev;
    });
  };

  const activeClassName = state?.simulatedYearOffset && state.simulatedYearOffset > 0 && state.futureYears 
    ? state.futureYears.find(fy => fy.isCurrentSim)?.className 
    : state?.studentData?.className;

  const resetSimulation = () => {
    loadSnapshot(); // Reloads real data
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0 overflow-hidden bg-gray-50 dark:bg-gray-900">
        <DialogHeader className="px-6 py-4 border-b bg-white dark:bg-gray-800">
          <div className="flex justify-between items-center">
             <div>
                <DialogTitle>Payment Logic Simulator</DialogTitle>
                <DialogDescription>
                  Simulating for <span className="font-semibold text-blue-600">{studentName}</span>. 
                  <br/>Changes here do <span className="text-red-500 font-bold">NOT</span> affect the database.
                </DialogDescription>
             </div>
             <Button variant="outline" size="sm" onClick={resetSimulation} className="gap-2">
               <RotateCcw className="h-4 w-4"/> Reset
             </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          
          {/* LEFT: Controls & Transactions */}
          <div className="w-full md:w-1/3 border-r bg-white dark:bg-gray-800 flex flex-col">
             <div className="p-4 border-b">
               <h3 className="font-semibold mb-3 flex items-center gap-2">
                 <Plus className="h-4 w-4"/> Add Mock Payment
               </h3>
               <div className="space-y-3">
                 <div>
                   <Label>Amount</Label>
                   <Input 
                      value={amount} 
                      onChange={e => setAmount(e.target.value)} 
                      type="number" 
                      placeholder="e.g. 5000"
                   />
                 </div>
                 <div>
                   <Label>Method</Label>
                   <select 
                     className="w-full border rounded-md p-2 bg-transparent"
                     value={method}
                     onChange={e => setMethod(e.target.value)}
                   >
                     <option value="cash">Cash</option>
                     <option value="mpesa">M-Pesa</option>
                     <option value="bank">Bank</option>
                   </select>
                 </div>
                 <Button onClick={addMockPayment} className="w-full bg-blue-600 hover:bg-blue-700">Simulate Payment</Button>
               </div>
               
               <div className="mt-6 pt-4 border-t">
                  <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm text-purple-700 dark:text-purple-400">
                    Time Travel
                  </h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Fast-forward the calendar to see how balances naturally shift when the next term officially begins.
                  </p>
                  <Button 
                     onClick={promoteTerm} 
                     variant="outline" 
                     className="w-full border-purple-200 text-purple-700 hover:bg-purple-50 dark:border-purple-800 dark:text-purple-300 dark:hover:bg-purple-900/30"
                     disabled={
                        state?.simulatedTerm === 'term3' && 
                        (!state?.futureChain || (state?.simulatedYearOffset || 0) >= state.futureChain.length)
                     }
                  >
                     Promote To Next Term / Class
                  </Button>
                  {state?.simulatedTerm && (
                     <div className="mt-2 text-center text-xs font-bold text-amber-600 bg-amber-50 p-1 rounded border border-amber-200">
                       Time Jump: {state.simulatedTerm.toUpperCase()} 
                       {state.simulatedYearOffset ? ` (+${state.simulatedYearOffset} Years)` : ''}
                     </div>
                  )}
               </div>
             </div>
             
             <div className="flex-1 overflow-y-auto p-4">
                <h3 className="font-semibold mb-3 text-sm text-gray-500 flex items-center gap-2">
                  <History className="h-4 w-4"/> Transaction History (Simulated)
                </h3>
                <div className="space-y-2">
                   {state?.transactions.map(tx => (
                     <div key={tx.id} className={`p-3 rounded border flex justify-between items-center ${tx.isMock ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800' : 'bg-gray-50 dark:bg-gray-800/50'}`}>
                        <div>
                           <p className="font-medium text-sm flex items-center gap-2">
                             {tx.isMock && <span className="text-[10px] bg-amber-200 text-amber-800 px-1 rounded uppercase">Mock</span>}
                             {tx.method}
                           </p>
                           <p className="text-xs text-gray-500">{tx.date.toLocaleDateString()}</p>
                        </div>
                        <span className={`font-bold ${tx.type === 'debit' ? 'text-red-500' : 'text-green-600'}`}>
                           {tx.type === 'debit' ? '-' : '+'} {tx.amount.toLocaleString()}
                        </span>
                     </div>
                   ))}
                </div>
             </div>
          </div>

          {/* RIGHT: Results Preview */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-950">
             {loading ? (
                <div className="h-full flex items-center justify-center flex-col gap-4">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500"/>
                  <p>Loading snapshot...</p>
                </div>
             ) : (
                <div className="space-y-6">
                   {/* Summary Cards */}
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Card>
                        <CardContent className="p-4">
                           <p className="text-sm text-muted-foreground">Opening Balance</p>
                           <h3 className="text-2xl font-bold">{(forcedOpeningBalance !== undefined ? forcedOpeningBalance : (state?.studentData?.outstandingBalance || 0)).toLocaleString()}</h3>
                        </CardContent>
                      </Card>
                      <Card className="bg-blue-600 text-white">
                        <CardContent className="p-4">
                           <p className="text-sm text-blue-100">Active Due ({activeClassName})</p>
                           <h3 className="text-2xl font-bold">
                             {state?.calculatedFees?.totalOutstanding?.toLocaleString()}
                           </h3>
                        </CardContent>
                      </Card>
                   </div>
                   
                   <h3 className="font-bold text-lg border-b pb-2 flex items-center gap-2">
                     {state?.simulatedYearOffset && state.simulatedYearOffset > 0 && (
                        <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs uppercase shadow-sm">Simulated Future</span>
                     )}
                     {activeClassName}
                   </h3>

                   {/* Term Breakdown - Similar to Guardian View */}
                   <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {(['term1', 'term2', 'term3'] as const).map(term => {
                         const tx = state?.calculatedFees?.[term];
                         if (!tx) return null;
                         
                         return (
                           <Card key={term} className="border-t-4 border-t-blue-500">
                             <CardContent className="p-4 pt-6 space-y-3">
                                <div className="flex justify-between items-center">
                                   <h4 className="font-bold text-lg uppercase">{term.replace('term', 'Term ')}</h4>
                                   <span className={`px-2 py-1 rounded text-xs font-bold ${tx.status.color}`}>{tx.status.label}</span>
                                </div>
                                <div className="space-y-1 text-sm">
                                   <div className="flex justify-between"><span>Required:</span> <span>{tx.required.toLocaleString()}</span></div>
                                   <div className="flex justify-between text-gray-500"><span>Balance from Prev Term:</span> <span>{tx.carryover.toLocaleString()}</span></div>
                                   <div className="flex justify-between font-semibold border-t pt-1"><span>Total Due:</span> <span>{tx.totalDue.toLocaleString()}</span></div>
                                   <div className="flex justify-between text-green-600"><span>Paid:</span> <span>({tx.paid.toLocaleString()})</span></div>
                                   <div className="flex justify-between font-bold text-lg pt-2 border-t">
                                     <span>Balance:</span> 
                                     <span className={tx.balance > 0 ? "text-red-500" : "text-green-500"}>
                                       {tx.balance > 0 ? tx.balance.toLocaleString() : `(CR) ${Math.abs(tx.balance).toLocaleString()}`}
                                     </span>
                                   </div>
                                </div>
                             </CardContent>
                           </Card>
                         )
                      })}
                   </div>
                   
                    {state?.futureYears && state.futureYears.some(fy => fy.isPredicted) && (
                      <div className="mt-8 space-y-6">
                        <div className="p-4 bg-green-50 border border-green-200 rounded text-green-800 text-sm mb-4">
                          🎉 <strong>Fully Paid for {activeClassName}!</strong> 
                          Demonstrating automatic rollover across future academic years. Credit carried forward sequentially.
                        </div>
                        
                        {state.futureYears.filter(fy => fy.isPredicted).map((futureYear, index) => (
                           <div key={`future-${index}`} className="opacity-90 transition-all">
                             <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                                Simulated Future Year <span className="text-gray-500 font-normal text-sm">({futureYear.className})</span>
                             </h3>
                             <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 border-t-2 border-dashed border-gray-300 pt-6">
                                {(['term1', 'term2', 'term3'] as const).map(term => {
                                   const tx = futureYear.calculatedFees[term];
                                   if (!tx) return null;
                                   
                                   return (
                                     <Card key={`next-${term}`} className="border-t-4 border-t-purple-500 border border-gray-200 bg-purple-50/10">
                                       <CardContent className="p-4 pt-6 space-y-3">
                                          <div className="flex justify-between items-center">
                                             <h4 className="font-bold text-lg uppercase">{term.replace('term', 'Term ')}</h4>
                                             <span className={`px-2 py-1 rounded text-xs font-bold ${tx.status.color}`}>{tx.status.label}</span>
                                          </div>
                                          <div className="space-y-1 text-sm">
                                             <div className="flex justify-between"><span>Required:</span> <span>{tx.required.toLocaleString()}</span></div>
                                             <div className="flex justify-between text-gray-500"><span>Rollover From Prev:</span> <span>{tx.carryover.toLocaleString()}</span></div>
                                             <div className="flex justify-between font-semibold border-t pt-1"><span>Total Due:</span> <span>{tx.totalDue.toLocaleString()}</span></div>
                                             <div className="flex justify-between text-purple-600"><span>Paid (Future):</span> <span>({tx.paid.toLocaleString()})</span></div>
                                             <div className="flex justify-between font-bold text-lg pt-2 border-t">
                                               <span>Balance:</span> 
                                               <span className={tx.balance > 0 ? "text-red-500" : "text-green-500"}>
                                                 {tx.balance > 0 ? tx.balance.toLocaleString() : `(CR) ${Math.abs(tx.balance).toLocaleString()}`}
                                               </span>
                                             </div>
                                          </div>
                                       </CardContent>
                                     </Card>
                                   )
                                })}
                             </div>
                           </div>
                        ))}
                      </div>
                    )}
                    
                   <div className="p-4 bg-amber-50 border border-amber-200 rounded text-amber-800 text-sm flex gap-2">
                      <AlertTriangle className="h-5 w-5 shrink-0"/>
                      <div>
                        <strong>Simulator Mode Active:</strong> These figures are generated partially clientside. 
                        They are designed to mimic the exact logic of the finance dashboard. 
                        Verify the logic here before trusting the main dashboard if in doubt.
                      </div>
                   </div>

                </div>
             )}
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentSimulator;
